import { useEffect, useMemo, useState, useCallback } from 'react';
import { connect, ConnectedProps } from 'react-redux';

// import { PlaceObject } from '@eventbrite/consumer-search.discover-utils/types';
// import { FormattedEvent } from '@eventbrite/consumer-search.event-renderer';
import { GlobalHeader, type GlobalHeaderProps } from '@eventbrite/consumer-header.global-header';
import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import { TagManager } from '@eventbrite/webapp-data';

import { resolveHeaderCreatorVariant, useHeaderCreateEventsSettings } from '@/src/homepage/bsp/config';
import {
    getHeaderCtaName,
    HEADER_CTA_CLICKED_EVENT,
    HEADER_CREATOR_EXPERIMENT_NAME,
} from '@/src/homepage/constants/picoxEvents';
import { ErrorBoundary } from '@/src/homepage/components/ErrorBoundary';
import { Footer, type FooterProps } from '@/src/homepage/components/Footer';

import { TAG_MANAGER_ENVIRONMENT } from '@/src/homepage/constants';
import { BrowseContainer } from '@/src/homepage/containers/BrowseContainer';
import { HomeBottomShelfContainer } from '@/src/homepage/containers/HomeBottomShelfContainer';
import { getRestOfContext } from '@/src/homepage/redux/root/';
import { DiscoverState } from '@/src/homepage/shared/types';
import type { BaseProps } from '@/src/homepage/types';
import { useClientAuth } from '@/src/homepage/common/useUserPublicId';
import { Stage } from '@eventbrite/webapp-constants';
import styles from './HomePageContainer.module.scss';
import { HeroBanner } from '@/src/homepage/components/HeroBanner';
import { SplitValues } from '@/src/homepage/utils/split';
import { useIsMobileDevice, getWindowObject } from '@eventbrite/legacy-infra.feature-detection';
import { getQueryParam } from '@/src/homepage/utils/queryString';
import { SMALL_WIDTH } from '@/src/homepage/common/breakpoints';
import { TLD_UNITED_KINGDOM } from '@/src/homepage/shared/constants/constants';
import {
    CompletionType,
    DEFAULT_COMPLETION_TYPES,
} from '@eventbrite/consumer-search.search-bar/constants';
import { AppContextProvider } from '@/src/homepage/common/context';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/src/homepage/components/guides/utils/queryClient';
import type { Guides } from '@/src/homepage/types/guides';

interface OwnProps extends Omit<BaseProps, 'publicUserId'> {
    guides?: Guides;
    // buckets?: Bucket[];
    // featureFlags?: FeatureFlags;
    splitValues: SplitValues;
    // placeId?: string;
    // latitude?: number;
    // longitude?: number;
    // currentPlace?: string;
    // currentPlaceParent?: string;
    // locations?: {
    //     places: PlaceObject[];
    // };
    // placeType?: string;
    // slug?: string;
    // flatBucket?: {
    //     results: FormattedEvent[];
    // };
    // handleRestOfContext?: Function;
    // originalContext?: object;
    // history?: object;
    // updateHeaderImage?: Function;
    // enable_experimentation?: boolean;
    // should_enable_experimentation?: boolean;
    // isGDPRCountry?: boolean;
    globalHeaderProps: GlobalHeaderProps;
    footerProps: FooterProps;
}
export type HomePageProps = OwnProps & ReduxProps;

// The tag manager bakes its user id into the GTM data layer, and `next/script`
// injects that inline script once on mount. The homepage document is
// shared-cached, so a server-rendered id is stale (it belongs to whoever
// populated the cache); hold the tag until the client resolves the real one.
function ClientTagManager({ stage }: { stage: Stage }) {
    const { authState, publicUserId } = useClientAuth();
    if (authState === 'unknown') {
        return null;
    }
    return (
        <TagManager
            publicUserId={publicUserId ?? ''}
            stage={stage}
            environment={TAG_MANAGER_ENVIRONMENT}
        />
    );
}

export function HomePage(props: HomePageProps) {
    const {
        env,
        stage,
        splitValues,
        globalHeaderProps,
        footerProps,
        handleRestOfContext,
    } = props;
    const [hasHydrated, setHasHydrated] = useState(false);
    useEffect(() => {
        setHasHydrated(true);
    }, []);
    const { settings: headerCreateEventsSettings, isLoading: isHeaderCreateEventsLoading } =
        useHeaderCreateEventsSettings();
    const creatorVariant = resolveHeaderCreatorVariant(
        headerCreateEventsSettings,
        isHeaderCreateEventsLoading,
        !hasHydrated,
    );

    // `header_cta_clicked` PicoX tracking for the logged-out global header.
    // `usePicoXTrack` no-ops outside a BspProvider; the homepage mounts one in
    // `pages/_app.page.tsx`. The callback is only wired for anonymous visitors
    // because `GlobalHeader` only renders the clickable logged-out nav then.
    const trackPicoX = usePicoXTrack();
    const onHeaderCtaClick = useCallback(
        (heapIdentifier: string, path: string) => {
            const cta = getHeaderCtaName(heapIdentifier);
            if (!cta) {
                return;
            }
            try {
                trackPicoX(HEADER_CTA_CLICKED_EVENT, {
                    cta,
                    surface: 'consumer_header_logged_out',
                    device:
                        typeof window !== 'undefined' && window.innerWidth < 480
                            ? 'mobile'
                            : 'desktop',
                    destination: path,
                    ...(creatorVariant !== 'control' && {
                        variant: creatorVariant,
                        experiment_name: HEADER_CREATOR_EXPERIMENT_NAME,
                    }),
                });
            } catch {
                // Tracking must never break navigation.
            }
        },
        [trackPicoX, creatorVariant],
    );

    const [headerKey, setHeaderKey] = useState(0);

    const isMobileDevice = useIsMobileDevice(SMALL_WIDTH);
    const [chatbotEnabled, setChatbotEnabled] = useState(false);

    // EB-323470: per-user Zendesk help-menu gating. The flag must be evaluated
    // per user, but the homepage SSR HTML is shared-cached, so it can't be baked
    // at SSR. Instead fetch it from the uncached /home/api/zendesk-menu endpoint
    // after hydration. We intentionally do NOT gate on a server-rendered user id:
    // the document is shared-cached, so any id baked into it is stale, and gating
    // on it would hide the menu for logged-in users served a cache populated by an
    // anonymous request. The endpoint reads the real per-request user and returns false
    // (without a Harness call, so no MTK) when logged out, so it is the
    // authoritative source. Still gated on en_US (eb-lang is in the CDN cache key,
    // so the SSR locale is reliable) to avoid unnecessary requests.
    const [showZendeskMenu, setShowZendeskMenu] = useState(false);
    useEffect(() => {
        if (env.localeInfo.locale !== 'en_US') {
            return;
        }
        let cancelled = false;
        fetch('/home/api/zendesk-menu', { cache: 'no-store' })
            .then((res) => res.json())
            .then((data) => {
                if (!cancelled) {
                    setShowZendeskMenu(Boolean(data?.showZendeskMenu));
                }
            })
            .catch(() => {
                // Fail closed: keep the legacy Help Center entry.
            });
        return () => {
            cancelled = true;
        };
    }, [env.localeInfo.locale]);

    const isBackForward = () => {
        const navEntries =
            (performance.getEntriesByType('navigation') as PerformanceNavigationTiming[]) || [];
        const nav = navEntries[0];
        return nav && nav.type === 'back_forward';
    };
    const restoredFromBFCache = (e: { persisted: boolean }) => {
        try {
            return Boolean(e?.persisted) || isBackForward();
        } catch (_err) {
            return false;
        }
    };
    const _handlePageShow = (e: { persisted: boolean }) => {
        if (restoredFromBFCache(e)) {
            setHeaderKey((k) => k + 1);
        }
    };

    // Always include organizer autocomplete completions.
    const _searchBarProps = useMemo(() => {
        return {
            chatbotEnabled,
            completionTypes: [...DEFAULT_COMPLETION_TYPES, CompletionType.ORGANIZER],
            pageType: 'Feed',
        };
    }, [chatbotEnabled]);

    useEffect(() => {
        // Chatbot search is off by default. Keep opt-out (set by
        // @eventbrite/consumer-search.chatbot) and optional
        // `?nlsearch-opt-in=true` for TStaff / manual testing.
        const optOutKey = 'nlsearch-experiment-opt-out';
        const localStorage = getWindowObject('localStorage');
        const optOutValue = localStorage?.getItem(optOutKey);

        // This condition is necessary due to a bug in rendering where the `useIsMobileDevice`
        // hook is not returning the correct value on initial render.
        if (!isMobileDevice || optOutValue === 'true') {
            setChatbotEnabled(false);
            return;
        }

        const overrideFlag = getQueryParam('nlsearch-opt-in', 'false') === 'true';
        setChatbotEnabled(overrideFlag);
    }, [isMobileDevice]);

    useEffect(() => {
        handleRestOfContext();
    }, []);

    // const eventGroups = createEventGroupFixture(consumerCardTestEvent);
    return (
        <main className={styles.root}>
            <AppContextProvider locale={env.localeInfo.locale}>
                <ErrorBoundary componentName="GlobalHeader">
                    <GlobalHeader
                        {...globalHeaderProps}
                        isHomePage={true}
                        creatorVariant={creatorVariant}
                        key={headerKey}
                        onCtaClick={onHeaderCtaClick}
                        featureFlags={{
                            showHelpCenterMenu: splitValues.showHelpCenterMenu,
                            showZendeskMenu,
                        }}
                        searchBar={_searchBarProps}
                    />
                </ErrorBoundary>
                {/* TODO handle seasonality properly */}

                <ErrorBoundary componentName="HeroBanner">
                    <HeroBanner
                        showGuides={!!props.guides}
                        locale={env.localeInfo.locale}
                        initialGuides={props.guides}
                        littleVeniceSpotlightEnabled={
                            splitValues.littleVeniceSpotlightEnabled &&
                            env.tld === TLD_UNITED_KINGDOM
                        }
                    />
                </ErrorBoundary>

                <ErrorBoundary componentName="BrowseContainer">
                    <QueryClientProvider client={queryClient}>
                        <BrowseContainer />
                    </QueryClientProvider>
                </ErrorBoundary>

                <ErrorBoundary componentName="TagManager">
                    <QueryClientProvider client={queryClient}>
                        <ClientTagManager stage={stage} />
                    </QueryClientProvider>
                </ErrorBoundary>
                <ErrorBoundary componentName="HomeBottomShelfContainer">
                    <HomeBottomShelfContainer
                        ebDomain={env.ebDomain}
                        tld={env.tld}
                        languageSubdirectory=""
                        localeInfo={env.localeInfo}
                        geoSeoLinksEnabled={splitValues.geoSeoLinksEnabled}
                    />
                </ErrorBoundary>
                <ErrorBoundary componentName="Footer">
                    <Footer {...footerProps} />
                </ErrorBoundary>
            </AppContextProvider>
        </main>
    );
}

const _mapHomeStateToProps = (state: DiscoverState) => {
    return {
        env: state.env,
    };
};

const _mapHomeDispatchToProps = {
    // handleSearch: runSearch,
    handleRestOfContext: getRestOfContext,
    // updateHeaderImage: updateHeaderImage,
};

export const connector = connect(_mapHomeStateToProps, _mapHomeDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export const HomePageContainer = connector(HomePage);
